/**
 * Журнал рассылок — разметка на базе legacy htdocs/core/templates/admin_statistic_log.tpl:
 * .add-form с вкладками ul.nav.nav-tabs (Email / Push / Telegram / MAX) и .tab-content.xs-zoom,
 * внутри — таблицы .table.table-hover со строками, разделёнными border-top: 2px solid #dddddd.
 * Вкладка SMS убрана: канал отключён (решение владельца).
 *
 * Данные: GET /api/admin/mailing/log (Handlers\Admin\Mailing::log) — лог email-рассылок
 * (email_logs), история push (rw_push_messages), Telegram (rw_telegram_messages) и MAX.
 *
 * Переключение вкладок в legacy делал bootstrap.js (data-toggle="tab"); в SPA его нет,
 * поэтому класс active переключается состоянием React — классы и разметка те же.
 * Таблица action_log (GET /api/admin/action_log) в legacy на экран не выводилась —
 * страница statistic_log показывала именно журнал рассылок; паритет сохранён (см. notes).
 */

import { useState, type CSSProperties } from 'react'

import { useLegacyCss } from '@/lib/legacyAssets'
import { useApi } from '@/lib/useApi'

/** Запись журнала email-рассылок (таблица email_logs). */
interface EmailLogRow {
  id: number
  date_post: string // unix-время строкой
  body_mail: string | null
  emails: string | null
}

/** Ответ GET /api/admin/mailing/log. */
/**
 * Отправленное push-уведомление (rw_push_messages).
 *
 * delivered — на сколько устройств реально ушло. 0 не значит «не отправляли»: у участка
 * может не быть приложения, телефон мог быть выключен. Поэтому показываем это отдельным
 * столбцом, а не прячем такие строки.
 */
interface PushLogRow {
  id: number
  title: string
  body: string
  kind: string
  delivered: number
  /** Что произошло: sent | no_devices | dry_run | no_key | failed. */
  status: string
  /** Причина, если не ушло. Для failed — ответ Google как есть. */
  error: string
  area_name: string | null
  created_at: string
}

/**
 * Отправленное сообщение в мессенджер: Telegram (rw_telegram_messages) или MAX
 * (rw_max_messages). Форма строки у них одна — и таблицы, и смысл статусов совпадают.
 *
 * Строка пишется на КАЖДУЮ отправку, включая неудачную, — как и у push. Поэтому здесь,
 * как и там, показываем не факт «ушло/не ушло», а ПРИЧИНУ: «у участка не подключён
 * мессенджер», «канал выключен в конфиге» и «сервер не ответил» — это три совершенно
 * разных повода что-то делать, а выглядели бы они одинаково.
 */
interface MessengerLogRow {
  id: number
  text: string
  kind: string
  /** Что произошло: sent | no_chats | dry_run | no_token | failed. */
  status: string
  /** Причина, если не ушло. Для failed — ответ Bot API как есть. */
  error: string
  area_name: string | null
  /** Аккаунт получателя — null, если чат уже отвязали. */
  username: string | null
  first_name: string | null
  created_at: string
}

interface MailingLogResponse {
  email: EmailLogRow[]
  push: PushLogRow[]
  telegram: MessengerLogRow[]
  max: MessengerLogRow[]
}

/**
 * Почему уведомление не ушло. Раньше все причины выглядели одинаково («не доставлено»),
 * и админ шёл гадать: приложения нет? ключ не тот? Google отверг?
 */
const PUSH_STATUS: Record<string, { label: string; css: string }> = {
  sent: { label: 'доставлено', css: 'label label-success' },
  no_devices: { label: 'нет приложения', css: 'label label-default' },
  dry_run: { label: 'канал выключен', css: 'label label-warning' },
  no_key: { label: 'нет ключа Firebase', css: 'label label-danger' },
  failed: { label: 'ошибка FCM', css: 'label label-danger' },
}

/** Откуда прилетело уведомление — по этому полю понятно, что именно рассылали. */
const PUSH_KIND: Record<string, string> = {
  invoice: 'Счёт',
  kvitancia: 'Квитанция',
  mailing: 'Рассылка',
  vote: 'Голосование',
  news: 'Новость',
}

/**
 * Почему сообщение в мессенджер не ушло (см. Services\TelegramNotify и Services\MaxNotify).
 * Статусы у обоих каналов одни и те же — и записываются они одинаково.
 */
const MESSENGER_STATUS: Record<string, { label: string; css: string }> = {
  sent: { label: 'доставлено', css: 'label label-success' },
  no_chats: { label: 'мессенджер не подключён', css: 'label label-default' },
  dry_run: { label: 'канал выключен', css: 'label label-warning' },
  no_token: { label: 'нет токена бота', css: 'label label-danger' },
  failed: { label: 'ошибка Bot API', css: 'label label-danger' },
}

/**
 * Обе таблицы журнала — фиксированной раскладки и по ширине контейнера.
 *
 * Без этого колонка «Текст» распирала таблицу до ~3700 px при окне 1176 px и вся
 * страница уезжала в горизонтальный скролл: в теле письма (email_logs.body_mail)
 * и в списке номеров лежат длинные строки без пробелов, а table-layout: auto
 * подгоняет колонку под самую длинную из них.
 */
const LOG_TABLE: CSSProperties = {
  tableLayout: 'fixed',
  width: '100%',
}

/** Длинные строки (HTML письма, JSON-массив номеров) переносятся, а не растягивают колонку. */
const LOG_CELL: CSSProperties = {
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  verticalAlign: 'top',
}

/** unix-время → «15.03.2024 12:34:56» (legacy date_format:'%d.%m.%Y %T'). */
function unixDateTime(value: string): string {
  const seconds = Number.parseInt(value, 10)
  if (Number.isNaN(seconds)) return value
  const date = new Date(seconds * 1000)
  const pad = (n: number): string => String(n).padStart(2, '0')
  return (
    `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  )
}

export function AdminActionLogPage() {
  useLegacyCss('admin', 'newapproach')

  const { data } = useApi<MailingLogResponse>('/api/admin/mailing/log')
  const [tab, setTab] = useState<'email' | 'push' | 'telegram' | 'max'>('email')

  const email = data?.email ?? []
  const push = data?.push ?? []
  const telegram = data?.telegram ?? []
  const max = data?.max ?? []

  return (
    <>
      <div className="row">
        <div className="col-xs-6" />
      </div>
      <div className="add-form">
        <ul className="nav nav-tabs" role="tablist">
          <li role="presentation" className={tab === 'email' ? 'active' : undefined}>
            <a
              href="#email"
              aria-controls="email"
              role="tab"
              data-toggle="tab"
              onClick={(e) => {
                e.preventDefault()
                setTab('email')
              }}
            >
              Email
            </a>
          </li>
          <li role="presentation" className={tab === 'push' ? 'active' : undefined}>
            <a
              href="#push"
              aria-controls="push"
              role="tab"
              data-toggle="tab"
              onClick={(e) => {
                e.preventDefault()
                setTab('push')
              }}
            >
              Push
            </a>
          </li>
          <li role="presentation" className={tab === 'telegram' ? 'active' : undefined}>
            <a
              href="#telegram"
              aria-controls="telegram"
              role="tab"
              data-toggle="tab"
              onClick={(e) => {
                e.preventDefault()
                setTab('telegram')
              }}
            >
              Telegram
            </a>
          </li>
          <li role="presentation" className={tab === 'max' ? 'active' : undefined}>
            <a
              href="#max"
              aria-controls="max"
              role="tab"
              data-toggle="tab"
              onClick={(e) => {
                e.preventDefault()
                setTab('max')
              }}
            >
              MAX
            </a>
          </li>
        </ul>

        <div className="tab-content xs-zoom">
          <div
            role="tabpanel"
            className={`tab-pane${tab === 'email' ? ' active' : ''}`}
            id="email"
          >
            <div className="row">
              <div className="col-xs-12">
                <table className="table table-hover" style={LOG_TABLE}>
                  <thead className="thead-dark">
                    <tr>
                      <th style={{ width: 170 }}>Дата</th>
                      <th>Текст</th>
                      <th style={{ width: 320 }}>Email`s</th>
                    </tr>
                  </thead>
                  <tbody>
                    {email.map((item) => (
                      <tr key={item.id} style={{ borderTop: '2px solid #dddddd' }}>
                        <td style={LOG_CELL}>{unixDateTime(item.date_post)}</td>
                        <td style={LOG_CELL}>{item.body_mail}</td>
                        <td style={LOG_CELL}>{item.emails}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div
            role="tabpanel"
            className={`tab-pane${tab === 'push' ? ' active' : ''}`}
            id="push"
          >
            <div className="row">
              <div className="col-xs-12">
                <table className="table table-hover" style={LOG_TABLE}>
                  <thead className="thead-dark">
                    <tr>
                      <th style={{ width: 150 }}>Дата</th>
                      <th style={{ width: 110 }}>Повод</th>
                      <th style={{ width: 90 }}>Участок</th>
                      <th>Уведомление</th>
                      <th style={{ width: 260 }}>Результат</th>
                    </tr>
                  </thead>
                  <tbody>
                    {push.length === 0 && (
                      <tr>
                        <td style={LOG_CELL} colSpan={5}>
                          Push-уведомлений ещё не отправляли.
                        </td>
                      </tr>
                    )}
                    {push.map((item) => (
                      <tr key={item.id} style={{ borderTop: '2px solid #dddddd' }}>
                        <td style={LOG_CELL}>{item.created_at.slice(0, 16).replace('T', ' ')}</td>
                        <td style={LOG_CELL}>{PUSH_KIND[item.kind] ?? item.kind}</td>
                        <td style={LOG_CELL}>
                          {item.area_name !== null ? `№${item.area_name}` : '—'}
                        </td>
                        <td style={LOG_CELL}>
                          <b>{item.title}</b>
                          {item.body !== '' && (
                            <>
                              <br />
                              {item.body}
                            </>
                          )}
                        </td>
                        {/*
                          Показываем не голое число, а ПРИЧИНУ: ноль устройств может
                          означать «приложения нет», «канал выключен в конфиге» или
                          «Google отверг запрос» — и это совсем разные поводы для действий.
                        */}
                        <td style={LOG_CELL}>
                          {(() => {
                            const state = PUSH_STATUS[item.status] ?? {
                              label: item.delivered > 0 ? 'доставлено' : 'не доставлено',
                              css: 'label label-default',
                            }
                            return (
                              <>
                                <span className={state.css}>{state.label}</span>
                                {item.delivered > 0 && ` · устройств: ${item.delivered}`}
                                {item.error !== '' && (
                                  <>
                                    <br />
                                    <small className="text-muted">{item.error}</small>
                                  </>
                                )}
                              </>
                            )
                          })()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <MessengerLog id="telegram" name="Telegram" rows={telegram} active={tab === 'telegram'} />
          <MessengerLog id="max" name="MAX" rows={max} active={tab === 'max'} />
        </div>
      </div>
    </>
  )
}

/**
 * История отправок в мессенджер — ОДНА таблица на Telegram и MAX.
 *
 * Копий здесь быть не должно: у каналов совпадает и форма строки, и набор статусов, и
 * смысл колонок. Две копии разъехались бы при первой же правке — а правят здесь ровно то,
 * ради чего вкладка и нужна: объяснение, ПОЧЕМУ сообщение не дошло.
 */
function MessengerLog({
  id,
  name,
  rows,
  active,
}: {
  id: string
  name: string
  rows: MessengerLogRow[]
  active: boolean
}) {
  return (
    <div role="tabpanel" className={`tab-pane${active ? ' active' : ''}`} id={id}>
      <div className="row">
        <div className="col-xs-12">
          <table className="table table-hover" style={LOG_TABLE}>
            <thead className="thead-dark">
              <tr>
                <th style={{ width: 150 }}>Дата</th>
                <th style={{ width: 110 }}>Повод</th>
                <th style={{ width: 90 }}>Участок</th>
                <th>Сообщение</th>
                <th style={{ width: 260 }}>Результат</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td style={LOG_CELL} colSpan={5}>
                    В {name} ещё ничего не отправляли.
                  </td>
                </tr>
              )}
              {rows.map((item) => (
                <tr key={item.id} style={{ borderTop: '2px solid #dddddd' }}>
                  <td style={LOG_CELL}>{item.created_at.slice(0, 16).replace('T', ' ')}</td>
                  <td style={LOG_CELL}>{PUSH_KIND[item.kind] ?? item.kind}</td>
                  <td style={LOG_CELL}>
                    {item.area_name !== null ? `№${item.area_name}` : '—'}
                    {/* Кому именно ушло: у участка может быть несколько чатов (муж, жена). */}
                    {item.username !== null && item.username !== '' && (
                      <>
                        <br />
                        <small className="text-muted">@{item.username}</small>
                      </>
                    )}
                  </td>
                  <td style={LOG_CELL}>{item.text}</td>
                  {/*
                    Как и у push — не «не доставлено», а ПРИЧИНА: мессенджер не подключён,
                    канал выключен в конфиге или Bot API отверг запрос.
                  */}
                  <td style={LOG_CELL}>
                    {(() => {
                      const state = MESSENGER_STATUS[item.status] ?? {
                        label: item.status === '' ? 'не доставлено' : item.status,
                        css: 'label label-default',
                      }
                      return (
                        <>
                          <span className={state.css}>{state.label}</span>
                          {item.error !== '' && (
                            <>
                              <br />
                              <small className="text-muted">{item.error}</small>
                            </>
                          )}
                        </>
                      )
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
